import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { endOfDay, startOfDay } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShipmentsList, {
  ShipmentListItem,
  ShipmentsListProps,
} from "../../../shipment/ShipmentsList/ShipmentsList";
import { testIds } from "./AutoPlanModal.testIds";

export type AutoPlanModalProps = {
  isOpen: boolean;
  shipments: ShipmentListItem[];
  onClose: () => void;
  onPlan: (startDate: Date, endDate: Date, shipmentIds: string[]) => void;
  currentPeriod: { start: Date; end: Date };
};

const AutoPlanModal = ({
  isOpen,
  shipments,
  onClose,
  onPlan,
  currentPeriod,
}: AutoPlanModalProps) => {
  const { t } = useTranslation("planning");

  const [filteredShipmentIds, setFilteredShipmentIds] = useState<string[]>([]);

  // Use calendar period directly for filtering shipments
  const startDate = startOfDay(currentPeriod.start);
  const endDate = endOfDay(currentPeriod.end);

  const shipmentsInDateRange = useMemo(
    () =>
      shipments.filter((shipment) => {
        const firstPickupTime = shipment.route?.firstPickupTime
          ? new Date(shipment.route.firstPickupTime)
          : null;
        const lastDropoffTime = shipment.route?.lastDropoffTime
          ? new Date(shipment.route.lastDropoffTime)
          : null;

        // Skip shipments without valid dates
        if (!firstPickupTime || !lastDropoffTime) {
          return false;
        }
        // A shipment should appear if it overlaps with the date range
        // This means: shipment starts before/on the period end AND ends after/on the period start
        // This ensures multi-day shipments appear on all days they span
        return firstPickupTime <= endDate && lastDropoffTime >= startDate;
      }),
    [shipments, startDate, endDate]
  );

  const shipmentsListQuery: ShipmentsListProps["query"] = useMemo(
    () => () => ({
      data: {
        shipments: {
          data: shipmentsInDateRange,
          count: shipmentsInDateRange.length,
        },
      },
      isLoading: false,
    }),
    [shipmentsInDateRange]
  );

  const onFilteredShipmentsChange = useCallback(
    (shipments: ShipmentListItem[]) => {
      setFilteredShipmentIds(shipments.map((s) => s._id));
    },
    []
  );

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("autoPlanModal.title")}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            py: 2,
          }}
        >
          <ShipmentsList
            tableId="autoPlanModal-shipments"
            query={shipmentsListQuery}
            selectable={false}
            disablePagination
            disableStatusTab
            excludePastShipments
            readonly
            onFilteredShipmentsChange={onFilteredShipmentsChange}
            useClientSideFiltering
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-testid={testIds.autoPlanModalClose} onClick={onClose}>{t("autoPlanModal.cancel")}</Button>
        <Button data-testid={testIds.autoPlanModalConfirm}
          onClick={() => {
            onPlan(startDate, endDate, filteredShipmentIds);
            onClose();
          }}
        >
          {t("autoPlanModal.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoPlanModal;
